import './perks-wallet-card.component.scss';

import { h } from 'preact';
import { useEffect, useState } from 'preact/hooks';

import { selectAccountSettings } from '../../../auth/store/auth.selectors';
import { QRComponent } from '../../../codes/components/qr.component';
import { AppleWalletBtn } from '../../../common/components/wallet/apple-wallet-btn.component';
import { GoogleWalletBtn } from '../../../common/components/wallet/google-wallet-btn.component';
import { useTranslation } from '../../../i18n';
import { WalletType } from '../../../membership/models/membership.model';
import { membershipService } from '../../../membership/services/membership.service';
import { selectMemberships } from '../../../membership/store/membership.selectors';
import type { AccountSettingsResponse } from '../../../models/common.model';
import { useAppSelector } from '../../../store/hooks';
import { isAndroidMobile, isMobile } from '../../../utils/platform.utils';

/**
 * Permanent (non-dismissable) "Download Your Membership Card" section for the
 * My Account page (SNGL-7029). Same content as the carousel's wallet card —
 * QR on desktop, wallet button on mobile — but always present, so a fan who
 * dismissed the carousel card still has a discoverable path to their pass.
 * Honors the admin's "Promote Card" setting (showClaimWalletPass).
 */
export const PerksWalletCard = () => {
  const { t } = useTranslation();
  const memberships = useAppSelector(selectMemberships);
  const accountSettingsResp: AccountSettingsResponse = useAppSelector(selectAccountSettings);
  const { accountSettings } = accountSettingsResp || {};

  const contract =
    memberships?.find((m) => m.contract?.tier?.walletTheme?.enabled)?.contract ?? null;
  const walletEnabled = !!contract?.tier?.walletTheme?.enabled;
  const showWallet = walletEnabled && accountSettings?.showClaimWalletPass !== false;

  const [walletPassUrl, setWalletPassUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!walletEnabled || !contract) return;
    if (isMobile()) {
      const walletType = isAndroidMobile() ? WalletType.GOOGLE : WalletType.APPLE;
      membershipService.getWalletPassUrl(contract, walletType).then((result) => {
        setWalletPassUrl(result.url);
      });
    } else {
      // Desktop renders a QR scanned by an unknown phone. Encode the app-proxy
      // scan URL directly: the backend resolves Apple vs Google from the
      // SCANNING phone's User-Agent and creates the pass at scan time.
      setWalletPassUrl(
        `${window.location.origin}/apps/single/v3/wallet/download/contract/${contract.id}`
      );
    }
  }, [walletEnabled, contract?.id]);

  if (!showWallet) {
    return <></>;
  }

  return (
    <div class="single-wallet-section">
      <div class="single-wallet-inner">
        {!isMobile() && (
          <div class="single-wallet-qr">{walletPassUrl && <QRComponent text={walletPassUrl} />}</div>
        )}
        <div class="single-wallet-content">
          <h4 class="single-card-title">{t('perks.walletCardTitle')}</h4>
          <p class="single-wallet-pass-desc">{t('perks.walletPassDescription')}</p>
          {(!isMobile() || !walletPassUrl) && (
            <>
              <br />
              <p>{t('perks.walletScanDescription')}</p>
            </>
          )}
          {isMobile() && walletPassUrl && (
            <div class="single-wallet-mobile-btn">
              {isAndroidMobile() ? (
                <GoogleWalletBtn
                  onClick={() => {
                    window.location.href = walletPassUrl;
                  }}
                />
              ) : (
                <AppleWalletBtn
                  onClick={() => {
                    window.location.href = walletPassUrl;
                  }}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
